import  React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
const { 
  FiFileText, FiDownload, FiPresentation, FiPieChart, 
  FiAlertTriangle, FiCheckCircle, FiChevronRight, FiPrinter,
  FiShare2, FiLayers, FiActivity, FiShield, FiTrendingUp,
  FiLayout, FiTarget, FiZap, FiBarChart2
} = FiIcons;
import ReportPage from '../components/reports/ReportPage';

export const ReportGenerator = () => {
  const location = useLocation();
const queryParams = new URLSearchParams(location.search);
const reportType = queryParams.get('type') || 'all';
const reportTitles = {
  all: 'Alle rapporter',
  we: 'WE Rapport',
  department: 'Avdelingsrapporter',
  leader: 'Lederrapporter'
};

const currentReportTitle = reportTitles[reportType] || 'Alle rapporter';
  const [isGenerating, setIsGenerating] = useState(false);
  const [viewMode, setViewMode] = useState('preview'); // preview | list

  const handleExport = (format) => {
    setIsGenerating(true);
    setTimeout(() => {
      setIsGenerating(false);
      alert(`${format} er generert og lastes ned.`);
    }, 2000);
  };

  return (
    <div className="max-w-7xl mx-auto space-y-8 pb-20">
      {/* Sticky Header Control */}
      <div className="sticky top-4 z-50 bg-white/80 backdrop-blur-xl p-4 rounded-2xl border border-slate-200 shadow-xl flex items-center justify-between">
        <div className="flex items-center gap-4 px-4">
          <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center text-white">
            <SafeIcon icon={FiFileText} size={20} />
          </div>
          <div>
            <h2 className="text-sm font-black uppercase tracking-tight text-slate-900 leading-none">
  {currentReportTitle}
</h2>
            <p className="text-[10px] font-bold text-slate-500 uppercase mt-1">TechCorp Norway - Q1 2024</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button 
            onClick={() => handleExport('PDF')}
            className="px-4 py-2 bg-slate-900 text-white rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all flex items-center gap-2"
          >
            <SafeIcon icon={FiDownload} /> Last ned PDF
          </button>
          <button 
            onClick={() => handleExport('Board Summary')}
            className="px-4 py-2 bg-indigo-50 text-indigo-700 rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-indigo-100 transition-all flex items-center gap-2"
          >
            <SafeIcon icon={FiShield} /> Styreoppsummering
          </button>
        </div>
      </div>

      {/* Report Canvas */}
      <div className="bg-slate-50 rounded-[3rem] p-12 border border-slate-200/50">
        
        {/* Page 1: Forside */}
        <ReportPage pageNumber={1} title="Forside">
          <div className="h-full flex flex-col justify-center items-center text-center space-y-8">
            <div className="w-32 h-32 bg-slate-900 rounded-3xl flex items-center justify-center text-white mb-8">
              <SafeIcon icon={FiPieChart} size={64} />
            </div>
            <h1 className="text-5xl font-black text-slate-900 uppercase tracking-tighter leading-none">
              Organisasjonshelse <br /> & Psykososial <br /> <span className="text-indigo-600">Risikoanalyse</span>
            </h1>
            <div className="w-20 h-1 bg-indigo-600 rounded-full" />
            <div className="space-y-1">
              <p className="text-xl font-bold text-slate-900 uppercase tracking-widest">TechCorp Norway AS</p>
              <p className="text-sm font-bold text-slate-400 uppercase tracking-[0.3em]">Mars 2024</p>
            </div>
          </div>
        </ReportPage>

        {/* Page 2: Executive Summary */}
        <ReportPage pageNumber={2} title="Executive Summary">
          <h3 className="text-2xl font-black text-slate-900 uppercase tracking-tight mb-8">1. Executive Summary</h3>
          <div className="space-y-6 text-slate-600 leading-relaxed font-medium">
            <p>
              Denne rapporten presenterer resultatene fra den psykososiale kartleggingen gjennomført i Q1 2024. 
              Med en svarprosent på 88% gir datagrunnlaget en høy grad av representativitet for organisasjonen.
            </p>
            <div className="p-8 bg-indigo-50 rounded-3xl border border-indigo-100">
              <h4 className="text-indigo-900 font-black uppercase text-xs tracking-widest mb-4">Hovedkonklusjon</h4>
              <p className="text-indigo-800 text-lg leading-snug">
                Organisasjonen viser sterk kulturell motstandskraft, men identifiserer kritiske utfordringer knyttet til 
                <strong> rolleuklarhet</strong> og <strong>stressbelastning</strong> i skjæringspunktet mellom utvikling og support.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-6 mt-8">
              <div className="space-y-4">
                <h5 className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">Styrker</h5>
                <ul className="space-y-2">
                  <li className="flex gap-2 text-xs font-bold text-slate-700">
                    <SafeIcon icon={FiCheckCircle} className="text-emerald-500 shrink-0" />
                    Høy tillit til nærmeste leder
                  </li>
                  <li className="flex gap-2 text-xs font-bold text-slate-700">
                    <SafeIcon icon={FiCheckCircle} className="text-emerald-500 shrink-0" />
                    Godt kollegialt samhold
                  </li>
                </ul>
              </div>
              <div className="space-y-4">
                <h5 className="text-[10px] font-black text-rose-600 uppercase tracking-widest">Risiko</h5>
                <ul className="space-y-2">
                  <li className="flex gap-2 text-xs font-bold text-slate-700">
                    <SafeIcon icon={FiAlertTriangle} className="text-rose-500 shrink-0" />
                    Tegn til emosjonell utmattelse
                  </li>
                  <li className="flex gap-2 text-xs font-bold text-slate-700">
                    <SafeIcon icon={FiAlertTriangle} className="text-rose-500 shrink-0" />
                    Uklare ansvarsforhold i matrisen
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </ReportPage>

        {/* Page 3: Workplace Scores */}
        <ReportPage pageNumber={3} title="Workplace Scores">
          <h3 className="text-2xl font-black text-slate-900 uppercase tracking-tight mb-8">2. Workplace Scores</h3>
          <div className="grid gap-8">
            {[
              { label: 'Psykologisk Trygghet', score: 78, trend: '+4', color: 'indigo' },
              { label: 'Konfliktrisiko', score: 22, trend: '-2', color: 'rose' },
              { label: 'Lederstøtte', score: 81, trend: '+1', color: 'emerald' },
              { label: 'Arbeidskrav', score: 68, trend: '+12', color: 'amber' }
            ].map((metric, i) => (
              <div key={i} className="space-y-3">
                <div className="flex justify-between items-end">
                  <span className="text-sm font-black text-slate-900 uppercase tracking-tight">{metric.label}</span>
                  <div className="text-right">
                    <span className="text-2xl font-black text-slate-900">{metric.score}</span>
                    <span className="text-[10px] font-bold text-slate-400 ml-1">/ 100</span>
                  </div>
                </div>
                <div className="w-full h-4 bg-slate-100 rounded-full overflow-hidden">
                  <motion.div 
                    initial={{ width: 0 }}
                    whileInView={{ width: `${metric.score}%` }}
                    className={`h-full rounded-full bg-${metric.color}-600`}
                  />
                </div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                  Trend: <span className={metric.trend.startsWith('+') ? 'text-emerald-600' : 'text-rose-600'}>{metric.trend}% fra forrige måling</span>
                </p>
              </div>
            ))}
          </div>
        </ReportPage>

        {/* Page 4: Department Comparison */}
        <ReportPage pageNumber={4} title="Avdelingssammenligning">
          <h3 className="text-2xl font-black text-slate-900 uppercase tracking-tight mb-8">3. Avdelingsanalyse</h3>
          <div className="space-y-6">
            <div className="overflow-hidden rounded-2xl border border-slate-100">
              <table className="w-full text-left">
                <thead className="bg-slate-900 text-white">
                  <tr>
                    <th className="p-4 text-[10px] font-black uppercase tracking-widest">Avdeling</th>
                    <th className="p-4 text-[10px] font-black uppercase tracking-widest">Helse</th>
                    <th className="p-4 text-[10px] font-black uppercase tracking-widest">Stress</th>
                    <th className="p-4 text-[10px] font-black uppercase tracking-widest">Trygghet</th>
                  </tr>
                </thead>
                <tbody className="text-xs font-bold text-slate-700">
                  <tr className="border-b border-slate-50">
                    <td className="p-4 uppercase">Salg & Marked</td>
                    <td className="p-4 text-emerald-600">84</td>
                    <td className="p-4 text-amber-600">42</td>
                    <td className="p-4">79</td>
                  </tr>
                  <tr className="border-b border-slate-50 bg-rose-50/30">
                    <td className="p-4 uppercase">Produktutvikling</td>
                    <td className="p-4 text-rose-600">62</td>
                    <td className="p-4 text-rose-600">78</td>
                    <td className="p-4">58</td>
                  </tr>
                  <tr className="border-b border-slate-50">
                    <td className="p-4 uppercase">Administrasjon</td>
                    <td className="p-4 text-emerald-600">91</td>
                    <td className="p-4 text-emerald-600">21</td>
                    <td className="p-4">88</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="p-6 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
              <p className="text-[10px] font-bold text-slate-500 italic">
                * Røde felter indikerer avvik på mer enn 15% fra organisasjonens gjennomsnitt. 
                Produktutvikling krever umiddelbar oppfølging.
              </p>
            </div>
          </div>
        </ReportPage>

        {/* Page 5: 90-Day Plan */}
        <ReportPage pageNumber={5} title="Handlingsplan">
          <h3 className="text-2xl font-black text-slate-900 uppercase tracking-tight mb-8">4. 90-Dagers Forbedringsplan</h3>
          <div className="space-y-12 relative before:absolute before:left-[19px] before:top-2 before:bottom-0 before:w-0.5 before:bg-indigo-100">
            {[
              { title: 'Fase 1: Diagnose & Dialog', weeks: 'Uke 1-3', icon: FiTarget, tasks: ['Gjennomføre fokusgruppeintervjuer', 'Lederworkshop: Rolleavklaring'] },
              { title: 'Fase 2: Intervensjon', weeks: 'Uke 4-8', icon: FiZap, tasks: ['Implementere nye kommunikasjonslinjer', 'Kurs i Psykologisk Trygghet'] },
              { title: 'Fase 3: Evaluering', weeks: 'Uke 9-12', icon: FiBarChart2, tasks: ['Re-testing av puls-måling', 'Styringsgruppemøte: Effektmåling'] }
            ].map((phase, i) => (
              <div key={i} className="relative pl-12">
                <div className="absolute left-0 top-0 w-10 h-10 bg-white border-2 border-indigo-600 rounded-xl flex items-center justify-center text-indigo-600 z-10 shadow-sm">
                  <SafeIcon icon={phase.icon} />
                </div>
                <div>
                  <span className="text-[10px] font-black text-indigo-600 uppercase tracking-[0.2em]">{phase.weeks}</span>
                  <h4 className="text-lg font-black text-slate-900 uppercase mt-1 mb-4">{phase.title}</h4>
                  <div className="space-y-2">
                    {phase.tasks.map((task, j) => (
                      <div key={j} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-slate-100 text-[11px] font-bold text-slate-600">
                        <div className="w-1.5 h-1.5 rounded-full bg-indigo-200" />
                        {task}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </ReportPage>

      </div>

      {/* Loading Overlay */}
      {isGenerating && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center">
          <div className="bg-white p-12 rounded-[2.5rem] text-center max-w-sm space-y-6 shadow-2xl border border-slate-100">
            <div className="relative w-20 h-20 mx-auto">
              <div className="absolute inset-0 border-4 border-indigo-100 rounded-full" />
              <div className="absolute inset-0 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
            </div>
            <div>
              <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">Genererer Rapport</h3>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-2">Kompilerer 14 sider med analyse...</p>
            </div>
            <div className="w-full h-1.5 bg-slate-50 rounded-full overflow-hidden">
              <motion.div 
                initial={{ width: 0 }}
                animate={{ width: '100%' }}
                transition={{ duration: 2 }}
                className="h-full bg-indigo-600"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportGenerator;